/**
 * Academic Period Date Validation Utilities
 * Provides validation functions for academic period dates based on calendar types
 */

import { CalendarType } from '@/services/calendarType.service';
import { AcademicPeriod } from '@/services/academicPeriod.service';
import DateUtils from '@/utils/date.utils';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface ValidationResult {
  isValid: boolean;
  message?: string;
  params?: Record<string, string>;
}

export const createLocalDate = (dateString: string): Date => {
  /**
   * Create a Date object from YYYY-MM-DD string or ISO timestamp.
   *
   * - If `dateString` is an ISO timestamp (contains `T`), we let the
   *   Date constructor handle it (the string is already in UTC).
   * - If `dateString` is in `YYYY-MM-DD` format, we interpret it as
   *   **midnight in the browser's local timezone**, not UTC.
   *   This avoids the typical "previous day" shift when comparing
   *   against UTC timestamps like `2026-01-01T03:00:00.000Z`.
   */
  // If it's an ISO timestamp, parse it directly (it's already in UTC)
  if (dateString.includes('T')) {
    return new Date(dateString);
  }

  // For YYYY-MM-DD format, interpret as midnight *local time* in the browser.
  // Example in UTC-3: 2026-01-01 → local 2026-01-01T00:00:00-03:00
  // and toISOString() → 2026-01-01T03:00:00.000Z
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

/**
 * Check whether an academic period has already started.
 *
 * A period counts as started once its start date is today or earlier: the
 * whole start day is considered "in progress". Both dates are compared as
 * UTC civil dates, matching AcademicPeriodService.hasStarted in backend-v2.
 *
 * @param startDate - Period start date (YYYY-MM-DD or ISO timestamp)
 * @param referenceDate - Date to compare against, defaults to now
 * @returns true when the period already started
 */
export const hasPeriodStarted = (
  startDate: string | null | undefined,
  referenceDate: Date = new Date(),
): boolean => {
  if (!startDate) {
    return false;
  }

  // Drop the time part first: an ISO timestamp like 2026-08-01T00:00:00.000Z
  // would otherwise land on Jul 31 in UTC-3.
  const normalized = DateUtils.normalizeDateToYYYYMMDD(startDate);
  if (!normalized) {
    return false;
  }

  // Compare the two civil dates as YYYY-MM-DD strings on a UTC basis, which is
  // exactly what the backend guard does (AcademicPeriodService.hasStarted uses
  // getUTC* components). Comparing local midnights instead would disagree with
  // the API for a few hours every evening in UTC-3.
  const today = referenceDate.toISOString().split('T')[0];

  return normalized <= today;
};

/**
 * Check whether an academic period has already ended.
 *
 * A period counts as ended once its end date is strictly before today: the
 * whole end day is still considered "in progress", mirroring the semantics
 * of hasPeriodStarted (whole start day counts as started).
 *
 * @param endDate - Period end date (YYYY-MM-DD or ISO timestamp)
 * @param referenceDate - Date to compare against, defaults to now
 * @returns true when the period has already ended
 */
export const hasPeriodEnded = (
  endDate: string | null | undefined,
  referenceDate: Date = new Date(),
): boolean => {
  if (!endDate) {
    return false;
  }

  const normalized = DateUtils.normalizeDateToYYYYMMDD(endDate);
  if (!normalized) {
    return false;
  }

  const today = referenceDate.toISOString().split('T')[0];

  return normalized < today;
};

/**
 * Check if a date is within a specific month/day range
 * @param date - Date string in YYYY-MM-DD format
 * @param startMonth - Start month (1-12)
 * @param startDay - Start day (1-31)
 * @param endMonth - End month (1-12)
 * @param endDay - End day (1-31)
 * @returns true if date is within range
 */
export const isDateInRange = (
  date: string,
  startMonth: number,
  startDay: number,
  endMonth: number,
  endDay: number,
): boolean => {
  // Use local date creation to avoid timezone shifts
  const d = createLocalDate(date);
  const month = d.getMonth() + 1; // getMonth() returns 0-11
  const day = d.getDate();

  // Handle ranges that cross year boundary (e.g., Nov-Jan)
  if (startMonth > endMonth) {
    return (
      (month > startMonth || (month === startMonth && day >= startDay))
      || (month < endMonth || (month === endMonth && day <= endDay))
    );
  }

  // Normal range within same year
  if (month < startMonth || month > endMonth) {
    return false;
  }
  if (month === startMonth && day < startDay) {
    return false;
  }
  if (month === endMonth && day > endDay) {
    return false;
  }

  return true;
};

// Tolerancia alrededor de la fecha oficial del tipo — a confirmar con
// producto/QA antes de shippear a prod (ver KB-834).
const VALIDATION_WINDOW_DAYS = 30;

const addDays = (month: number, day: number, delta: number): { month: number; day: number } => {
  // Año de referencia arbitrario (no bisiesto) — solo nos interesa mes/día.
  const d = new Date(2027, month - 1, day);
  d.setDate(d.getDate() + delta);
  return { month: d.getMonth() + 1, day: d.getDate() };
};

/**
 * Validate a period's start/end dates against a CalendarType record's own
 * start_day/start_month/end_day/end_month, with a fixed tolerance window —
 * instead of parsing the type's name for a letter (KB-834 point 43).
 */
export const validateByCalendarTypeBoundaries = (
  calendarType: Pick<CalendarType, 'startMonth' | 'startDay' | 'endMonth' | 'endDay'>,
  startDate: string,
  endDate: string,
): ValidationResult => {
  const startWindowFrom = addDays(
    calendarType.startMonth,
    calendarType.startDay,
    -VALIDATION_WINDOW_DAYS,
  );
  const startWindowTo = addDays(
    calendarType.startMonth,
    calendarType.startDay,
    VALIDATION_WINDOW_DAYS,
  );
  if (!isDateInRange(
    startDate,
    startWindowFrom.month,
    startWindowFrom.day,
    startWindowTo.month,
    startWindowTo.day,
  )) {
    return { isValid: false, message: 'validation_type_start' };
  }

  const endWindowFrom = addDays(
    calendarType.endMonth,
    calendarType.endDay,
    -VALIDATION_WINDOW_DAYS,
  );
  const endWindowTo = addDays(
    calendarType.endMonth,
    calendarType.endDay,
    VALIDATION_WINDOW_DAYS,
  );
  if (!isDateInRange(
    endDate,
    endWindowFrom.month,
    endWindowFrom.day,
    endWindowTo.month,
    endWindowTo.day,
  )) {
    return { isValid: false, message: 'validation_type_end' };
  }

  return { isValid: true };
};

/**
 * Validate that start date is before end date
 * @param startDate - Start date string in YYYY-MM-DD format
 * @param endDate - End date string in YYYY-MM-DD format
 * @returns Validation result
 */
export const validateStartBeforeEnd = (startDate: string, endDate: string): ValidationResult => {
  if (!startDate || !endDate) {
    return { isValid: false, message: 'Both dates are required' };
  }

  // Use local date creation to avoid timezone shifts
  const start = createLocalDate(startDate);
  const end = createLocalDate(endDate);

  if (start >= end) {
    return { isValid: false, message: 'validation_start_before_end' };
  }

  return { isValid: true };
};

/**
 * Validate Type A calendar dates
 * Start: Feb 1 - Apr 1
 * End: Nov 1 - Jan 1
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Validation result
 */
export const validateTypeA = (startDate: string, endDate: string): ValidationResult => {
  // Validate start date is between Feb 1 and Apr 1
  if (!isDateInRange(startDate, 2, 1, 4, 1)) {
    return { isValid: false, message: 'validation_type_a_start' };
  }

  // Validate end date is between Nov 1 and Jan 1 (crosses year boundary)
  if (!isDateInRange(endDate, 11, 1, 1, 1)) {
    return { isValid: false, message: 'validation_type_a_end' };
  }

  return { isValid: true };
};

/**
 * Validate Type B calendar dates
 * Start: Jun 1 - Sep 1
 * End: May 1 - Jul 1
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Validation result
 */
export const validateTypeB = (startDate: string, endDate: string): ValidationResult => {
  // Validate start date is between Jun 1 and Sep 1
  if (!isDateInRange(startDate, 6, 1, 9, 1)) {
    return { isValid: false, message: 'validation_type_b_start' };
  }

  // Validate end date is between May 1 and Jul 1
  if (!isDateInRange(endDate, 5, 1, 7, 1)) {
    return { isValid: false, message: 'validation_type_b_end' };
  }

  return { isValid: true };
};

/**
 * Validate Type C calendar dates
 * Start: Apr 1 - Jun 1
 * End: Jan 1 - Mar 1
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Validation result
 */
export const validateTypeC = (startDate: string, endDate: string): ValidationResult => {
  // Validate start date is between Apr 1 and Jun 1
  if (!isDateInRange(startDate, 4, 1, 6, 1)) {
    return { isValid: false, message: 'validation_type_c_start' };
  }

  // Validate end date is between Jan 1 and Mar 1
  if (!isDateInRange(endDate, 1, 1, 3, 1)) {
    return { isValid: false, message: 'validation_type_c_end' };
  }

  return { isValid: true };
};

/**
 * Validate dates based on calendar type name
 * @param calendarTypeName - Calendar type name (should contain A, B, or C)
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Validation result
 */
// DEPRECATED — sin callers desde KB-834 Task 6, se mantiene por ahora a pedido del plan.
export const validateByCalendarType = (
  calendarTypeName: string,
  startDate: string,
  endDate: string,
): ValidationResult => {
  // First check start is before end
  const basicValidation = validateStartBeforeEnd(startDate, endDate);
  if (!basicValidation.isValid) {
    return basicValidation;
  }

  // Determine type from name — use word boundaries for single-letter checks
  // to avoid false positives (e.g. "Calendario Tipo B" contains 'A' but is Type B)
  const upperName = calendarTypeName.toUpperCase();
  if (upperName.includes('TYPE A') || upperName.includes('TIPO A') || /\bA\b/.test(upperName)) {
    return validateTypeA(startDate, endDate);
  } if (upperName.includes('TYPE B') || upperName.includes('TIPO B') || /\bB\b/.test(upperName)) {
    return validateTypeB(startDate, endDate);
  } if (upperName.includes('TYPE C') || upperName.includes('TIPO C') || /\bC\b/.test(upperName)) {
    return validateTypeC(startDate, endDate);
  }

  // If no specific type found, just check start before end
  return { isValid: true };
};

/**
 * Validate custom period dates
 * Custom periods must:
 * 1. Start after the current default period's end date
 * 2. End before the next default period's start date + 1 year
 * @param startDate - Custom period start date
 * @param endDate - Custom period end date
 * @param currentDefaultEndDate - End date of current default period
 * @param nextDefaultStartDate - Start date of next default period
 * @returns Validation result
 */
export const validateCustomPeriod = (
  startDate: string,
  endDate: string,
  currentDefaultEndDate: string,
  nextDefaultStartDate: string,
): ValidationResult => {
  // First check start is before end
  const basicValidation = validateStartBeforeEnd(startDate, endDate);
  if (!basicValidation.isValid) {
    return basicValidation;
  }

  // Use local date creation to avoid timezone shifts
  const start = createLocalDate(startDate);
  const end = createLocalDate(endDate);
  const currentEnd = createLocalDate(currentDefaultEndDate);
  const nextStart = createLocalDate(nextDefaultStartDate);

  // Add one year to next default start date
  const nextStartPlusYear = new Date(nextStart);
  nextStartPlusYear.setFullYear(nextStartPlusYear.getFullYear() + 1);

  // Validate start is after current default end
  if (start <= currentEnd) {
    return { isValid: false, message: 'validation_custom_start' };
  }

  // Validate end is before next default start + 1 year
  if (end >= nextStartPlusYear) {
    return { isValid: false, message: 'validation_custom_end' };
  }

  return { isValid: true };
};

/**
 * Get the calendar type letter (A, B, or C) from a calendar type name
 * @param calendarTypeName - Calendar type name
 * @returns 'A', 'B', 'C', or null if not found
 */
export const getCalendarTypeLetter = (calendarTypeName: string): string | null => {
  const upperName = calendarTypeName.toUpperCase();
  if (upperName.includes('TYPE A') || upperName.includes('TIPO A') || /\bA\b/.test(upperName)) {
    return 'A';
  } if (upperName.includes('TYPE B') || upperName.includes('TIPO B') || /\bB\b/.test(upperName)) {
    return 'B';
  } if (upperName.includes('TYPE C') || upperName.includes('TIPO C') || /\bC\b/.test(upperName)) {
    return 'C';
  }
  return null;
};

/**
 * Validate that a candidate date range does not overlap any other academic
 * period of the same school. Two periods conflict if either boundary falls
 * inside the other's range, including sharing an exact start/end day — the
 * rest of this module already treats adjacent periods as required to differ
 * by at least one day (see validateCustomPeriod).
 *
 * @param candidate - Date range being created or edited
 * @param allPeriods - All academic periods of the school (default + custom)
 * @param excludeId - Id of the period being edited, to skip self-comparison
 * @returns Validation result; on conflict, params carry the conflicting
 *   period's name and normalized start/end dates for message interpolation
 */
export const validateNoOverlap = (
  candidate: DateRange,
  allPeriods: Pick<AcademicPeriod, 'id' | 'name' | 'startDate' | 'endDate'>[],
  excludeId?: number,
): ValidationResult => {
  const candidateStart = DateUtils.normalizeDateToYYYYMMDD(candidate.startDate);
  const candidateEnd = DateUtils.normalizeDateToYYYYMMDD(candidate.endDate);

  if (!candidateStart || !candidateEnd) {
    return { isValid: true };
  }

  const conflict = allPeriods.find((other) => {
    if (excludeId !== undefined && other.id === excludeId) {
      return false;
    }

    const otherStart = DateUtils.normalizeDateToYYYYMMDD(other.startDate);
    const otherEnd = DateUtils.normalizeDateToYYYYMMDD(other.endDate);
    if (!otherStart || !otherEnd) {
      return false;
    }

    return candidateStart <= otherEnd && candidateEnd >= otherStart;
  });

  if (!conflict) {
    return { isValid: true };
  }

  return {
    isValid: false,
    message: 'validation_overlap',
    params: {
      periodName: conflict.name,
      startDate: DateUtils.normalizeDateToYYYYMMDD(conflict.startDate) || conflict.startDate,
      endDate: DateUtils.normalizeDateToYYYYMMDD(conflict.endDate) || conflict.endDate,
    },
  };
};
